import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, ButtonGroup, Grid } from '@mui/material';

import { useLazyQuery, useMutation, useQuery, useQueryRefHandlers } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ImportExportIcon from '@mui/icons-material/ImportExport';

import PagedTable from 'components/table/PagedTable';
import { filterSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';

import { PUBLIC_CLIENT_LIST_SEARCH_QUERY, PUBLIC_COMPANIES_LIST_SEARCH_QUERY } from './gql';

import { ErrorAlert, RedirectError } from 'pages/error';
import SimpleTable from 'components/table/SimpleTable';
import { HL_PublicClientOutput, HL_PublicCompanyOutput, HLBookingListOutput, EOfferStatus } from '__generated__/graphql';
import { ASSIGN_BOOKING_MUTATION } from '../bookings/gql';

interface BookingsListProps {
  bookings: HLBookingListOutput[];
}

/**
 * Bookings list table component with assignment functionality.
 * Shows bookings with their associated offers, companies, clients, and hotels.
 * Allows Seminargo users to assign unassigned bookings to themselves.
 */
export default function BookingsList(props: BookingsListProps) {
  const navigate = useNavigate();
  const filter = filterSelector();
  const user = userSelector()!;

  const [assignBookingMutation, { loading: assignLoading }] = useMutation(ASSIGN_BOOKING_MUTATION);

  const handleAssignToMe = async (bookingId: number) => {
    if (!user.isSeminargo) {
      return;
    }
    try {
      await assignBookingMutation({
        variables: {
          bookingId,
          userId: user.id,
        },
      });
    } catch (e) {
      console.error('Failed to assign booking:', e);
    }
  };

  return (
    <>
      <Helmet>
        <title>{i18next.t('clients-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SimpleTable
            headers={[
              i18next.t('bookings-list-header-refcode'),
              i18next.t('bookings-list-header-title'),
              i18next.t('bookings-list-header-company'),
              i18next.t('bookings-list-header-client'),
              i18next.t('bookings-list-header-hotel'),
              i18next.t('bookings-list-header-assignee'),
            ]}
            rows={(props.bookings || []).map((row, index) => {
              const approvedOffer = row.offers?.find(offer =>
                offer.status === EOfferStatus.APPROVED ||
                offer.status === EOfferStatus.ACTIVE ||
                offer.status === EOfferStatus.INVOICE
              );
              const hotel = approvedOffer?.hotel;
              const assigneeName = row.responsibleUser ? row.responsibleUser.name : null;
              const canAssignToMe = user.isSeminargo && !row.responsibleUser;

              return [
                <Link to={`/bookings/${row.id}`}>{row.refCode}</Link>,
                row.title,
                row.publicCompany ? (
                  <Link to={`/clients/companies/${row.publicCompany.id}`}>{row.publicCompany.name}</Link>
                ) : (
                  '-'
                ),
                row.publicClient ? (
                  <Link to={`/clients/clients/${row.publicClient.id}`}>
                    {row.publicClient.firstname + ' ' + row.publicClient.lastname}
                  </Link>
                ) : (
                  '-'
                ),
                hotel ? <Link to={`/settings/hotels/${hotel.id}`}>{hotel.name}</Link> : '-',
                canAssignToMe ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{assigneeName}</span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleAssignToMe(row.id)}
                      disabled={assignLoading}
                    >
                      {i18next.t('booking-info-assign-to-me')}
                    </Button>
                  </Box>
                ) : (
                  assigneeName
                ),
              ];
            })}
          />
        </Grid>
      </Grid>
    </>
  );
}